import { create } from '@bufbuild/protobuf'
import type { SplitSupplyContract } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import {
  SplitSupplyForm,
  SplitSupplyNoticeBy,
  SplitSupplySelfRole,
} from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import {
  type Decimal,
  DecimalSchema,
} from '@ebalance/proto/google/type/decimal_pb'

import { protoDateToCivilDate, requireCivilDate } from '~/models/civilDate'
import type { BasePatternValue } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import {
  type SelfRoleChoice,
  type SplitSupplyFormValues,
  noticeByChoiceOf,
  noticeBySide,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/schema'

/** 新規登録の初期値。 供給形態・自社の立場は未選択から始める。 */
export function emptyFormValues(): SplitSupplyFormValues {
  return {
    supplyPointSpecificNumber: '',
    selfRole: '',
    loadFollowingBusinessMasterId: '',
    loadFollowingContractId: '',
    baseBusinessMasterId: '',
    baseContractId: '',
    basePriority: '',
    supplyForm: SplitSupplyForm.UNSPECIFIED,
    ownSlotFrom: '',
    ownSlotTo: '',
    noticeBy: '',
    baseContractKw: '',
    note: '',
    basePatterns: [],
  }
}

function selfRoleChoice(role: SplitSupplySelfRole): SelfRoleChoice {
  switch (role) {
    case SplitSupplySelfRole.LOAD_FOLLOWING:
      return 'loadFollowing'
    case SplitSupplySelfRole.BASE:
      return 'base'
    case SplitSupplySelfRole.UNSPECIFIED:
    default:
      return ''
  }
}

/** 取得した契約を編集フォームの値にする。 自社の立場は backend が導出した値を使う。 */
export function contractToFormValues(
  contract: SplitSupplyContract,
): SplitSupplyFormValues {
  const basePatterns: BasePatternValue[] = contract.basePatterns.map((p) => ({
    patternOrder: p.patternOrder,
    validFrom: requireCivilDate(
      protoDateToCivilDate(p.validFrom),
      `SplitSupplyBasePattern.validFrom (order=${p.patternOrder})`,
    ),
    validTo: protoDateToCivilDate(p.validTo),
    dayTypes: [...p.dayTypes],
    months: [...p.months],
    slotFrom: p.slotFrom,
    slotTo: p.slotTo,
    slotValues: p.slotValues.map(Number),
  }))
  return {
    supplyPointSpecificNumber: contract.supplyPointSpecificNumber,
    selfRole: selfRoleChoice(contract.selfRole),
    loadFollowingBusinessMasterId:
      contract.loadFollowing?.businessMasterId ?? '',
    loadFollowingContractId: contract.loadFollowingContractId ?? '',
    baseBusinessMasterId: contract.base?.businessMasterId ?? '',
    baseContractId: contract.baseContractId ?? '',
    basePriority:
      contract.basePriority === undefined ? '' : String(contract.basePriority),
    supplyForm: contract.supplyForm,
    ownSlotFrom:
      contract.ownSlotFrom === undefined ? '' : String(contract.ownSlotFrom),
    ownSlotTo:
      contract.ownSlotTo === undefined ? '' : String(contract.ownSlotTo),
    // 保存値は変動型だけ意味を持つ (他の形態は内部でベース分に正規化されている)
    noticeBy:
      contract.supplyForm === SplitSupplyForm.VARIABLE
        ? noticeByChoiceOf(
            contract.noticeBy === SplitSupplyNoticeBy.LOAD_FOLLOWING
              ? 'loadFollowing'
              : 'base',
            selfRoleChoice(contract.selfRole),
          )
        : '',
    baseContractKw: contract.baseContractKw?.value ?? '',
    note: contract.note,
    basePatterns,
  }
}

/** 変動型の 自社 / 他社 を、 自社の立場との組み合わせでベース分 / 負荷追随分にする (未選択は UNSPECIFIED)。 */
function noticeByToProto(values: SplitSupplyFormValues): SplitSupplyNoticeBy {
  switch (noticeBySide(values.noticeBy, values.selfRole)) {
    case 'base':
      return SplitSupplyNoticeBy.BASE
    case 'loadFollowing':
      return SplitSupplyNoticeBy.LOAD_FOLLOWING
    case undefined:
    default:
      return SplitSupplyNoticeBy.UNSPECIFIED
  }
}

const orUndefined = (v: string): string | undefined =>
  v === '' ? undefined : v

/**
 * フォームの値から Create / Update 共通の request field を作る (flat)。
 * 空欄は「未入力」として送らない。 担当時間帯は時間分割型の時だけ、 通告値を出す側は変動型の時だけ (未選択は UNSPECIFIED)、
 * ベース分供給者の契約電力は固定型の時だけ送る (backend も同じ正規化をする)。
 * 立場は送らない (backend が両側の事業者から導く)。
 */
export function formValuesToRequestFields(values: SplitSupplyFormValues): {
  supplyForm: SplitSupplyForm
  loadFollowingBusinessMasterId: string
  loadFollowingContractId: string | undefined
  baseBusinessMasterId: string
  baseContractId: string | undefined
  basePriority: number | undefined
  ownSlotFrom: number | undefined
  ownSlotTo: number | undefined
  noticeBy: SplitSupplyNoticeBy
  baseContractKw: Decimal | undefined
  note: string
  basePatterns: Array<{
    patternOrder: number
    validFrom: BasePatternValue['validFrom']
    validTo: BasePatternValue['validTo']
    dayTypes: BasePatternValue['dayTypes']
    months: number[]
    slotFrom: number | undefined
    slotTo: number | undefined
    slotValues: bigint[]
  }>
} {
  const isTimeSplit = values.supplyForm === SplitSupplyForm.TIME_SPLIT
  const isVariable = values.supplyForm === SplitSupplyForm.VARIABLE
  const isFixed = values.supplyForm === SplitSupplyForm.FIXED
  return {
    supplyForm: values.supplyForm,
    loadFollowingBusinessMasterId: values.loadFollowingBusinessMasterId,
    loadFollowingContractId: orUndefined(values.loadFollowingContractId),
    baseBusinessMasterId: values.baseBusinessMasterId,
    baseContractId: orUndefined(values.baseContractId),
    basePriority:
      values.basePriority === '' ? undefined : Number(values.basePriority),
    ownSlotFrom:
      isTimeSplit && values.ownSlotFrom !== ''
        ? Number(values.ownSlotFrom)
        : undefined,
    ownSlotTo:
      isTimeSplit && values.ownSlotTo !== ''
        ? Number(values.ownSlotTo)
        : undefined,
    noticeBy: isVariable
      ? noticeByToProto(values)
      : SplitSupplyNoticeBy.UNSPECIFIED,
    baseContractKw:
      isFixed && values.baseContractKw.trim() !== ''
        ? create(DecimalSchema, { value: values.baseContractKw.trim() })
        : undefined,
    note: values.note,
    basePatterns: values.basePatterns.map((p) => ({
      patternOrder: p.patternOrder,
      validFrom: p.validFrom,
      validTo: p.validTo,
      dayTypes: p.dayTypes,
      months: p.months,
      slotFrom: p.slotFrom,
      slotTo: p.slotTo,
      slotValues: p.slotValues.map((v) => BigInt(v)),
    })),
  }
}
